// src/pages/BlogPost.tsx
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { getBlogPostBySlug } from "@/utils/blog-handlers";
import type { PostMeta } from "@/utils/blog-handlers";

// Load prerendered content
const loadPrerenderedContent = async (slug: string) => {
  try {
    const response = await fetch('/blog-content.json');
    const prerenderedPosts = await response.json();
    return prerenderedPosts.find((post: any) => post.slug === slug);
  } catch (error) {
    console.warn('Could not load prerendered content:', error);
    return null;
  }
};

const BlogPost = () => {
  const { slug = "" } = useParams();
  const [meta, setMeta] = useState<PostMeta | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [prerenderedContent, setPrerenderedContent] = useState<string | null>(null);

  useSEO({
    title: meta ? `${meta.title} | Talos Blog` : 'Post Not Found | Talos Blog',
    description: meta?.excerpt || 'The requested blog post could not be found.',
    keywords: 'insurance verification, healthcare automation, eligibility checks, EHR integration, Talos',
    canonical: meta ? `https://runtalos.com/blog/${meta.slug}` : undefined,
    ogTitle: meta?.title || 'Post Not Found',
    ogDescription: meta?.excerpt || 'The requested blog post could not be found.',
    ogType: 'article',
    ogUrl: meta ? `https://runtalos.com/blog/${meta.slug}` : undefined,
    twitterCard: 'summary_large_image',
    twitterTitle: meta?.title || 'Post Not Found',
    twitterDescription: meta?.excerpt || 'The requested blog post could not be found.',
    structuredData: meta ? {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": meta.title,
      "description": meta.excerpt,
      "author": {
        "@type": "Organization",
        "name": "Talos"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Talos",
        "logo": {
          "@type": "ImageObject",
          "url": "https://runtalos.com/public/logo.png"
        }
      },
      "datePublished": meta.date,
      "url": `https://runtalos.com/blog/${meta.slug}`
    } : undefined
  });

  useEffect(() => {
    let alive = true;
    
    (async () => {
      try {
        if (!slug) return;
        
        const prerendered = await loadPrerenderedContent(slug);
        if (prerendered && prerendered.content && alive) {
          setPrerenderedContent(prerendered.content);
          setMeta(prerendered.meta || null);
          setLoading(false);
          return;
        }
        
        const blogPost = await getBlogPostBySlug(slug);
        if (!blogPost) {
          throw new Error(`No blog post found with slug: ${slug}`);
        }
        
        if (!alive) return;
        
        setMeta(blogPost.meta);
        setContent(blogPost.content);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || "Failed to load post.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    
    return () => {
      alive = false;
    };
  }, [slug]);

  // Show skeleton while loading
  if (loading) {
    return (
      <div className="bg-white text-zinc-900 p-6 flex-1 flex">
        <div className="mx-auto w-full max-w-5xl">
          <Card className="rounded-lg border-0 p-0">
            <CardContent className="p-0">
              {/* Back button skeleton */}
              <div className="mb-6">
                <Skeleton className="h-4 w-24" />
              </div>

              {/* Title skeleton */}
              <Skeleton className="h-12 w-3/4 mb-3" />

              {/* Date skeleton */}
              <div className="mb-6 pb-6 border-b border-zinc-200">
                <Skeleton className="h-4 w-32" />
              </div>

              {/* Content skeletons */}
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <div className="my-8">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full mt-4" />
                  <Skeleton className="h-4 w-3/4 mt-4" />
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Only show "not found" if loading is complete and no meta was found
  if (!meta && !loading) {
    return (
      <div className="bg-white text-zinc-900 p-0 flex-1 flex" >
        <div className="mx-auto max-w-5xl w-full text-center">
          <h1 className="text-3xl font-semibold mb-4">Post not found</h1>
          <Link to="/blog">Back to Blog</Link>
        </div>
      </div>
    );
  }

  // Type guard: if we reach here and meta is null, return early
  if (!meta) {
    return null;
  }

  return (
    <div className="bg-white text-zinc-900 p-6 flex-1 flex">
        <div 
          className="mx-auto w-full max-w-5xl">
          <Card className="rounded-lg border-0 p-0">
            <CardContent className="p-0">
              {/* Back */}
              <div className="mb-6">
                <Link to="/blog" className="inline-flex items-center gap-2 text-zinc-700 underline hover:text-zinc-900 transition-colors">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Blog
                </Link>
              </div>

              {/* Title */}
              <h1 className="text-4xl font-semibold mb-3">{meta.title}</h1>

              {/* Meta */}
              {meta.date && (
                <div className="mb-6 uppercase tracking-wide text-zinc-500 border-b border-zinc-200 pb-6">
                  {meta.date}
                </div>
              )}

              {/* Body */}
              {error && (
                <p className="text-red-600">
                  {error} — ensure the file exists and the path/slug matches.
                </p>
              )}
              {(prerenderedContent || content) && (
                <div className="max-w-none">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h2: ({children}) => (
                        <h2 className="text-2xl font-bold mt-8 mb-4 text-zinc-900 pb-2">
                          {children}
                        </h2>
                      ),
                      h3: ({children}) => (
                        <h3 className="text-xl font-semibold mt-6 mb-3 text-zinc-900">
                          {children}
                        </h3>
                      ),
                      ul: ({children}) => (
                        <ul className="my-6 space-y-3 list-none">
                          {children}
                        </ul>
                      ),
                      li: ({children}) => (
                        <li className="flex items-start text-zinc-700 leading-relaxed">
                          <span className="text-zinc-400 mr-3 mt-1 text-lg">•</span>
                          <span className="flex-1 text-lg">{children}</span>
                        </li>
                      ),
                      p: ({children}) => (
                        <p className="mb-4 text-zinc-700 leading-relaxed text-lg">
                          {children}
                        </p>
                      ),
                      strong: ({children}) => (
                        <strong className="font-semibold text-zinc-900">
                          {children}
                        </strong>
                      ),
                      a: ({href, children}) => (
                        <a
                          href={href}
                          className="text-zinc-900 underline underline-offset-2 hover:text-zinc-700"
                        >
                          {children}
                        </a>
                      ),
                      hr: () => (
                        <hr className="my-8 border-0 border-t border-zinc-200" />
                      )
                    }}
                  >
                    {prerenderedContent || content}
                  </ReactMarkdown>
                </div>
              )}
              <Card className="rounded-lg w-full mt-12 mb-8 bg-white border-1">
                <CardContent className="p-6 text-center">
                  <div className="max-w-lg mx-auto">
                    <h3 className="text-xl font-regular text-zinc-900 mb-6">
                      Talos automates insurance eligibility verification. Want to learn more? Book a demo today.
                    </h3>
                    <Link 
                      to="/demo" 
                      className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2 rounded-lg text-lg font-medium hover:bg-zinc-800 transition-colors"
                    >
                      Book a Demo
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </div>
  );
};

export default BlogPost;
